import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import BasicInformationStep from "./components/BasicInformationStep";
import EmployeeStepper from "./components/EmployeeStepper";
import EmploymentDetailsStep from "./components/EmploymentDetailsStep";
import SalaryDetailsStep from "./components/SalaryDetailsStep";
import DocumentsStep from "./components/DocumentsStep";
import ReviewStep from "./components/ReviewStep";
import { employeeInputError } from './employeeInputValidation';

const genderMap = {
  Male: "MALE",
  Female: "FEMALE",
  Other: "OTHER",
};

export default function AddEmployee() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [currentStep, setCurrentStep] = useState(0);
  const [isLoadingMasters, setIsLoadingMasters] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  const [masters, setMasters] = useState({
    branches: [],
    departments: [],
    designations: [],
    roles: [],
    attendanceHolidayPolicies: [],
  });

  const [formData, setFormData] = useState({
    fullName: "",
    nickname: "",
    email: "",
    phone: "",
    password: "",
    gender: "",
    dob: "",

    department: "",
    designation: "",
    branch: "",
    joiningDate: "",
    employmentType: "",

    grantErpAccess: false,
    erpRoleIds: [],

    salaryType: "",
    basicSalary: "",
    ctc: "",
    overtimeEligible: false,
    overtimeRate: "",
    earlyExitDeductionEligible: false,
    earlyExitDeductionRate: "",
    commissionPercent: "",
    graceMinutes: "",

    shiftType: "",
    shiftStartTime: "",
    shiftEndTime: "",
    breakDurationMinutes: "",
    attendanceHolidayPolicyId: "",
    attendanceHolidayPolicyEffectiveFrom: "",

    paymentMode: "",
    bankName: "",
    accountHolderName: "",
    accountNumber: "",
    ifscCode: "",

    aadhaarNumber: "",
    panNumber: "",
  });

  const stepTitles = [
    "Basic Information",
    "Employment Details",
    "Salary Details",
    "Documents",
    "Review & Submit",
  ];

  const visibleRoles = useMemo(
    () => masters.roles.filter((role) => role.code !== "EMPLOYEE_SELF_SERVICE"),
    [masters.roles],
  );

  useEffect(() => {
    async function loadMasters() {
      if (!accessToken) return;

      try {
        setIsLoadingMasters(true);

        const [branches, departments, roles, attendanceHolidayPolicies] =
          await Promise.all([
            apiRequest("/branches", { accessToken }),
            apiRequest("/departments?activeOnly=true", { accessToken }),
            apiRequest("/roles", { accessToken }),
            apiRequest("/attendance-holiday-policies?activeOnly=true", {
              accessToken,
            }),
          ]);

        setMasters((previous) => ({
          ...previous,
          branches: branches || [],
          departments: departments || [],
          roles: roles || [],
          attendanceHolidayPolicies: attendanceHolidayPolicies || [],
        }));
      } catch (error) {
        setMessage(error.message || "Unable to load HRMS master data.");
      } finally {
        setIsLoadingMasters(false);
      }
    }

    loadMasters();
  }, [accessToken]);

  useEffect(() => {
    async function loadDesignations() {
      if (!accessToken) return;

      if (!formData.department) {
        setMasters((previous) => ({
          ...previous,
          designations: [],
        }));
        return;
      }

      try {
        const designations = await apiRequest(
          `/designations?activeOnly=true&departmentId=${formData.department}`,
          { accessToken },
        );

        setMasters((previous) => ({
          ...previous,
          designations: designations || [],
        }));
      } catch (error) {
        setMessage(error.message || "Unable to load designations.");
      }
    }

    loadDesignations();
  }, [accessToken, formData.department]);

  const validateCurrentStep = () => {
    const inputError = employeeInputError(formData);
    if (inputError) {
      setMessage(inputError);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    if (currentStep === 0) {
      if (!formData.fullName.trim()) {
        setMessage("Full name is required.");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return false;
      }

    }

    if (currentStep === 1) {
      if (
        !formData.department ||
        !formData.designation ||
        !formData.branch ||
        !formData.joiningDate ||
        !formData.employmentType
      ) {
        setMessage(
          "Department, designation, branch, joining date and employment type are required.",
        );
        window.scrollTo({ top: 0, behavior: "smooth" });
        return false;
      }

      if (formData.grantErpAccess && !formData.erpRoleIds.length) {
        setMessage(
          "Select at least one ERP role or turn off ERP dashboard access.",
        );
        window.scrollTo({ top: 0, behavior: "smooth" });
        return false;
      }
    }

    setMessage("");
    return true;
  };

  const nextStep = () => {
    if (!validateCurrentStep()) return;

    if (currentStep < 4) {
      setCurrentStep((previous) => previous + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const previousStep = () => {
    setMessage("");

    if (currentStep > 0) {
      setCurrentStep((previous) => previous - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const saveDraft = () => {
    setMessage(
      "Server-side employee drafts are not implemented yet. Nothing has been saved.",
    );
  };

  const optional = (value) => value?.trim() || undefined;

  const optionalNumber = (value) => {
    if (value === "" || value === null || value === undefined) {
      return undefined;
    }

    return Number(value);
  };

  const createEmployee = async () => {
    if (!validateCurrentStep()) return;

    const payload = {
      fullName: formData.fullName.trim(),
      nickname: optional(formData.nickname),
      email: optional(formData.email),
      phone: optional(formData.phone),
      password: optional(formData.password),

      gender: formData.gender ? genderMap[formData.gender] : undefined,
      dateOfBirth: formData.dob || undefined,

      branchId: formData.branch || undefined,
      departmentId: formData.department || undefined,
      designationId: formData.designation || undefined,
      joiningDate: formData.joiningDate || undefined,
      employmentType: formData.employmentType
        ? formData.employmentType.toUpperCase()
        : undefined,

      erpRoleIds: formData.grantErpAccess ? formData.erpRoleIds : undefined,

      salaryType: formData.salaryType || undefined,
      basicSalary: optionalNumber(formData.basicSalary),
      ctc: optionalNumber(formData.ctc),

      overtimeEligible: Boolean(formData.overtimeEligible),
      overtimeRate: optionalNumber(formData.overtimeRate),

      earlyExitDeductionEligible: Boolean(formData.earlyExitDeductionEligible),
      earlyExitDeductionRate: optionalNumber(formData.earlyExitDeductionRate),

      commissionPercent: optionalNumber(formData.commissionPercent),

      shiftType: optional(formData.shiftType),
      shiftStartTime: formData.shiftStartTime || undefined,
      shiftEndTime: formData.shiftEndTime || undefined,
      breakDurationMinutes: optionalNumber(formData.breakDurationMinutes),
      graceMinutes: optionalNumber(formData.graceMinutes),
      ...(formData.attendanceHolidayPolicyId
        ? {
            attendanceHolidayPolicyId: formData.attendanceHolidayPolicyId,
            attendanceHolidayPolicyEffectiveFrom:
              formData.attendanceHolidayPolicyEffectiveFrom ||
              formData.joiningDate ||
              undefined,
          }
        : {}),

      paymentMode: formData.paymentMode || undefined,
      bankName: optional(formData.bankName),
      accountHolderName: optional(formData.accountHolderName),
      accountNumber: optional(formData.accountNumber),
      ifscCode: optional(formData.ifscCode),

      aadhaarNumber: optional(formData.aadhaarNumber),
      panNumber: optional(formData.panNumber),
    };

    try {
      setIsSubmitting(true);
      setMessage("");

      await apiRequest("/employees", {
        method: "POST",
        body: payload,
        accessToken,
      });

      navigate("/admindashboard/employees");
    } catch (error) {
      const backendMessage = Array.isArray(error.details?.message)
        ? error.details.message.join(", ")
        : error.message;

      setMessage(backendMessage || "Unable to create employee.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pb-28">
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Add Employee</h1>

          <p className="mt-2 text-gray-500">
            Onboard new employees in a few simple steps.
          </p>
        </div>

        <button
          type="button"
          onClick={saveDraft}
          className="rounded-xl border border-gray-200 bg-white px-5 py-3 font-medium transition-all hover:bg-gray-50"
        >
          Save Draft
        </button>
      </div>

      {message ? (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {message}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
        <div>
          <EmployeeStepper currentStep={currentStep} />
        </div>

        <div className="lg:col-span-3">
          <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
            <div className="mb-8">
              <span className="text-sm font-medium text-red-800">
                Step {currentStep + 1} of 5
              </span>

              <h2 className="mt-2 text-2xl font-bold text-gray-900">
                {stepTitles[currentStep]}
              </h2>
            </div>

            {isLoadingMasters ? (
              <div className="py-16 text-center text-gray-500">
                Loading HRMS master data...
              </div>
            ) : (
              <>
                {currentStep === 0 ? (
                  <BasicInformationStep
                    formData={formData}
                    setFormData={setFormData}
                  />
                ) : null}

                {currentStep === 1 ? (
                  <EmploymentDetailsStep
                    formData={formData}
                    setFormData={setFormData}
                    branches={masters.branches}
                    departments={masters.departments}
                    designations={masters.designations}
                    roles={visibleRoles}
                  />
                ) : null}

                {currentStep === 2 ? (
                  <SalaryDetailsStep
                    formData={formData}
                    setFormData={setFormData}
                    attendanceHolidayPolicies={
                      masters.attendanceHolidayPolicies
                    }
                  />
                ) : null}

                {currentStep === 3 ? (
                  <DocumentsStep
                    formData={formData}
                    setFormData={setFormData}
                  />
                ) : null}

                {currentStep === 4 ? (
                  <ReviewStep
                    formData={formData}
                    branches={masters.branches}
                    departments={masters.departments}
                    designations={masters.designations}
                    roles={visibleRoles}
                  />
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white/95 px-4 py-4 shadow-[0_-8px_30px_rgba(0,0,0,0.08)] backdrop-blur lg:left-72 md:px-8">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <button
            type="button"
            onClick={previousStep}
            disabled={currentStep === 0 || isSubmitting}
            className="rounded-xl border border-gray-200 bg-white px-5 py-3 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>

          <div className="hidden text-sm text-gray-500 sm:block">
            Step {currentStep + 1} of 5 · {stepTitles[currentStep]}
          </div>

          {currentStep === 4 ? (
            <button
              type="button"
              onClick={createEmployee}
              disabled={isSubmitting || isLoadingMasters}
              className="rounded-xl bg-green-600 px-6 py-3 font-medium text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? "Creating Employee..." : "Create Employee"}
            </button>
          ) : (
            <button
              type="button"
              onClick={nextStep}
              disabled={isLoadingMasters}
              className="rounded-xl bg-red-800 px-6 py-3 font-medium text-white transition hover:bg-red-900 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
